import { 
  Award, Mic, Languages, PenTool, Globe, FileCheck,
  Shield, Zap, Lock, Clock, Database, BarChart3, 
  CheckCircle, Users, Brain, Sparkles, Target, Settings
} from 'lucide-react';
import { ProductConfigs } from './types';

export const languageTestingSolutionsConfigs: ProductConfigs = {
  'complete-language-assessment-certification': {
    keyBenefits: [
      {
        icon: Award,
        title: 'CEFR-Compliant Assessment Framework',
        description: 'Complete European language standards compliance with automated CEFR level determination and certification management.',
        metrics: '100% CEFR standards alignment',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Brain,
        title: 'Adaptive Testing Intelligence',
        description: 'AI-powered adaptive assessment that adjusts difficulty in real-time based on learner performance and competency.',
        metrics: '95% assessment accuracy',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Languages,
        title: 'Multi-Skill Evaluation System',
        description: 'Comprehensive assessment covering reading, writing, listening, speaking, grammar, and cultural competency.',
        metrics: '6-skill comprehensive evaluation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Language Platform Support',
        description: 'Support for 50+ languages with native assessment frameworks and cultural context integration.',
        metrics: '50+ languages supported',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Progress Analytics',
        description: 'Detailed learning analytics with skill gap identification, progress tracking, and personalized learning paths.',
        metrics: 'Personalized learning insights',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Professional Certification Management',
        description: 'Complete certification lifecycle management with digital badges, verification systems, and renewal tracking.',
        metrics: 'Fraud-proof certifications',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'AI-Powered Assessment Generation',
        description: 'Intelligent question generation with difficulty calibration, content variation, and bias detection.',
        metrics: 'Unlimited assessment variations',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Assessment Framework',
        description: 'Configurable assessment workflows with institution-specific requirements and custom scoring rubrics.',
        metrics: 'Unlimited customisation options',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Users,
        title: 'Institutional Integration Hub',
        description: 'Seamless integration with learning management systems, HR platforms, and educational institutions.',
        metrics: '500+ platform integrations',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: FileCheck,
        title: 'Proctoring & Security Suite',
        description: 'Advanced anti-cheating measures with AI proctoring, browser lockdown, and biometric verification.',
        metrics: '99.9% assessment integrity',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Cultural Competency Assessment',
        description: 'Advanced cultural context evaluation with business etiquette, communication styles, and regional awareness.',
        metrics: 'Comprehensive cultural evaluation',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Institutional Analytics Dashboard',
        description: 'Comprehensive institutional reporting with learner demographics, success rates, and comparative analysis.',
        metrics: 'Data-driven institutional insights',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Assessment Security Framework',
        description: 'Military-grade security with encrypted assessments, secure delivery, and tamper-proof results.',
        metrics: 'Bank-level assessment security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Assessment Engine',
        description: 'High-performance assessment delivery with instant scoring, real-time feedback, and responsive interface.',
        metrics: 'Millisecond response times',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Privacy-Compliant Testing',
        description: 'GDPR-compliant assessment platform with data protection, consent management, and privacy controls.',
        metrics: '100% privacy compliance',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'Scalable Testing Infrastructure',
        description: 'Global assessment infrastructure with auto-scaling, load balancing, and 99.99% uptime guarantee.',
        metrics: '99.99% platform availability',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Secure Data Management',
        description: 'Comprehensive data protection with encrypted storage, secure backup, and disaster recovery capabilities.',
        metrics: '100% data integrity assurance',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Quality Assurance System',
        description: 'Automated quality control with assessment validation, psychometric analysis, and accuracy verification.',
        metrics: '99.9% assessment quality',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  },

  'complete-audio-language-assessment': {
    keyBenefits: [
      {
        icon: Mic,
        title: 'Advanced Speech Recognition Engine',
        description: 'State-of-the-art speech analysis with pronunciation assessment, fluency evaluation, and accent detection.',
        metrics: '98% speech recognition accuracy',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Audio Analysis',
        description: 'Intelligent audio processing with natural language understanding, sentiment analysis, and conversational flow assessment.',
        metrics: 'Human-level audio comprehension',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Comprehensive Listening Assessment',
        description: 'Multi-level listening comprehension testing with contextual understanding, inference evaluation, and audio complexity scaling.',
        metrics: '360-degree listening evaluation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Accent Audio Library',
        description: 'Diverse audio content with native speaker recordings, regional accents, and authentic communication scenarios.',
        metrics: '100+ accent variations',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Real-Time Audio Analytics',
        description: 'Instant pronunciation feedback with detailed analysis of rhythm, intonation, stress patterns, and speaking clarity.',
        metrics: 'Real-time speech coaching',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Sparkles,
        title: 'Interactive Speaking Scenarios',
        description: 'Immersive speaking practice with AI conversation partners, role-playing exercises, and real-world simulations.',
        metrics: 'Engaging speaking practice',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Settings,
        title: 'Custom Audio Assessment Builder',
        description: 'Configurable audio assessments with custom content, difficulty scaling, and institution-specific requirements.',
        metrics: 'Unlimited audio customisation',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Languages,
        title: 'Phonetic Analysis Engine',
        description: 'Advanced phonetic evaluation with IPA transcription, sound pattern analysis, and pronunciation coaching.',
        metrics: 'Phonetic precision analysis',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Users,
        title: 'Conversational AI Integration',
        description: 'Interactive AI conversation partners with natural dialogue, contextual responses, and adaptive difficulty.',
        metrics: 'Natural conversation practice',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: FileCheck,
        title: 'Audio Quality Validation',
        description: 'Automated audio quality assessment with noise detection, clarity verification, and technical validation.',
        metrics: '99.9% audio quality assurance',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Mobile Audio Assessment',
        description: 'Native mobile applications with optimized audio recording, offline capabilities, and seamless synchronization.',
        metrics: 'Full mobile audio support',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Audio Learning Analytics',
        description: 'Comprehensive audio performance tracking with progress visualization, skill development insights, and coaching recommendations.',
        metrics: 'Audio learning optimization',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Audio Security & Privacy',
        description: 'Secure audio processing with encryption, privacy protection, and confidential recording management.',
        metrics: 'Complete audio security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'High-Performance Audio Engine',
        description: 'Real-time audio processing with low-latency analysis, instant feedback, and optimized performance.',
        metrics: '<100ms audio processing',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Secure Audio Storage',
        description: 'Encrypted audio storage with access controls, audit trails, and compliance-ready data management.',
        metrics: 'Military-grade audio protection',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'Scalable Audio Infrastructure',
        description: 'High-capacity audio processing with distributed computing, auto-scaling, and global performance optimization.',
        metrics: '99.99% audio system uptime',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Audio Data Management',
        description: 'Intelligent audio data handling with compression optimization, metadata management, and efficient storage.',
        metrics: 'Optimized audio data handling',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Audio Quality Assurance',
        description: 'Automated audio validation with technical compliance, clarity verification, and assessment integrity.',
        metrics: '99.9% audio assessment quality',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  },

  'writing-evaluation-platform': {
    keyBenefits: [
      {
        icon: PenTool,
        title: 'AI Writing Analysis Engine',
        description: 'Advanced writing assessment with grammar analysis, style evaluation, coherence scoring, and content quality measurement.',
        metrics: '95% writing assessment accuracy',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Brain,
        title: 'Intelligent Content Evaluation',
        description: 'Deep content analysis with argument structure assessment, evidence evaluation, and critical thinking measurement.',
        metrics: 'Comprehensive content analysis',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Automated Rubric Scoring',
        description: 'Customizable rubric-based assessment with multi-dimensional scoring and detailed performance feedback.',
        metrics: 'Multi-dimensional assessment',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: CheckCircle,
        title: 'Plagiarism Detection System',
        description: 'Advanced plagiarism detection with similarity analysis, source identification, and originality verification.',
        metrics: '99.9% plagiarism detection',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Detailed Feedback Reports',
        description: 'Comprehensive writing feedback with specific recommendations, improvement suggestions, and learning pathways.',
        metrics: 'Personalized writing coaching',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Sparkles,
        title: 'Style & Voice Analysis',
        description: 'Advanced style evaluation with tone analysis, voice consistency, and audience appropriateness assessment.',
        metrics: 'Professional style guidance',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Settings,
        title: 'Custom Assessment Framework',
        description: 'Configurable writing assessments with custom rubrics, scoring criteria, and institution-specific requirements.',
        metrics: 'Unlimited assessment customisation',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Languages,
        title: 'Multi-Language Writing Support',
        description: 'Comprehensive writing assessment across multiple languages with native language expertise and cultural context.',
        metrics: '25+ languages supported',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Users,
        title: 'Collaborative Review System',
        description: 'Peer review and instructor feedback integration with collaborative editing and multi-stakeholder assessment.',
        metrics: 'Enhanced collaborative assessment',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: FileCheck,
        title: 'Academic Integrity Monitoring',
        description: 'Comprehensive academic integrity with AI detection, submission tracking, and authenticity verification.',
        metrics: 'Complete integrity assurance',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Genre-Specific Assessment',
        description: 'Specialized assessment for different writing genres including academic, business, creative, and technical writing.',
        metrics: 'Genre-tailored evaluation',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Writing Analytics Dashboard',
        description: 'Comprehensive writing performance analytics with progress tracking, skill development, and improvement insights.',
        metrics: 'Data-driven writing development',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Writing Security & Privacy',
        description: 'Secure writing assessment with content protection, privacy compliance, and confidential submission handling.',
        metrics: 'Complete writing security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Writing Analysis',
        description: 'Instant writing feedback with real-time grammar checking, style suggestions, and performance optimization.',
        metrics: 'Real-time writing assistance',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Secure Document Management',
        description: 'Encrypted document storage with access controls, version tracking, and secure collaboration features.',
        metrics: 'Bank-level document security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'High-Performance Processing',
        description: 'Scalable writing analysis with distributed processing, optimized performance, and guaranteed response times.',
        metrics: '99.99% system availability',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Secure Content Repository',
        description: 'Protected writing repository with encrypted storage, backup systems, and disaster recovery capabilities.',
        metrics: '100% data protection assurance',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Assessment Quality Control',
        description: 'Automated quality assurance with assessment validation, accuracy verification, and continuous improvement.',
        metrics: '99.9% assessment reliability',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  },

  'multilingual-competency-platform': {
    keyBenefits: [
      {
        icon: Globe,
        title: 'Global Language Support',
        description: 'Comprehensive assessment platform supporting 50+ languages with native expertise and cultural integration.',
        metrics: '50+ languages with native support',
        color: 'from-green-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Cultural Competency Assessment',
        description: 'Advanced cultural intelligence evaluation with cross-cultural communication skills and cultural awareness testing.',
        metrics: 'Comprehensive cultural evaluation',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Award,
        title: 'International Certification Standards',
        description: 'Global certification compliance with international standards, regional requirements, and professional credentials.',
        metrics: 'Worldwide certification recognition',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Business Language Proficiency',
        description: 'Specialized business language assessment with industry-specific terminology and professional communication skills.',
        metrics: 'Business-ready language skills',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Languages,
        title: 'Cross-Cultural Communication',
        description: 'Advanced intercultural communication assessment with cultural sensitivity and adaptation skills evaluation.',
        metrics: 'Enhanced cultural intelligence',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Global Competency Analytics',
        description: 'Comprehensive global competency tracking with cultural proficiency metrics and international readiness assessment.',
        metrics: 'Global readiness insights',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'AI Cultural Intelligence Engine',
        description: 'Machine learning for cultural pattern recognition, bias detection, and personalized cultural learning paths.',
        metrics: 'AI-powered cultural insights',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Localized Assessment Framework',
        description: 'Region-specific assessment customisation with local standards, cultural norms, and regulatory compliance.',
        metrics: 'Culturally adapted assessments',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Globe,
        title: 'International Compliance Hub',
        description: 'Global regulatory compliance with international standards, regional certifications, and cross-border recognition.',
        metrics: 'Worldwide compliance assurance',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: FileCheck,
        title: 'Multi-Standard Certification',
        description: 'Support for multiple international standards including CEFR, ACTFL, ILR, and regional frameworks.',
        metrics: 'Universal standard support',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Users,
        title: 'Global Team Assessment',
        description: 'Team-based multilingual assessment with collaborative evaluation and cross-cultural team dynamics.',
        metrics: 'Enhanced team collaboration',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Brain,
        title: 'Adaptive Cultural Learning',
        description: 'Personalized cultural learning paths with adaptive content delivery and culturally relevant scenarios.',
        metrics: 'Personalized cultural development',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Global Security Standards',
        description: 'International security compliance with regional data protection laws and cross-border security protocols.',
        metrics: 'Worldwide security compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Multi-Region Performance',
        description: 'Global high-performance infrastructure with regional optimization and localized content delivery.',
        metrics: 'Worldwide performance optimization',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Cross-Border Data Protection',
        description: 'International data protection compliance with GDPR, regional privacy laws, and secure data transfer.',
        metrics: 'Global privacy compliance',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'Global Infrastructure Reliability',
        description: 'Worldwide infrastructure with multi-region deployment, disaster recovery, and 24/7 global support.',
        metrics: '99.99% global availability',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Multilingual Data Management',
        description: 'Advanced multilingual data handling with Unicode support, cultural metadata, and localized storage.',
        metrics: 'Complete multilingual support',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'International Quality Assurance',
        description: 'Global quality standards with international validation, cultural accuracy verification, and continuous improvement.',
        metrics: '99.9% global quality assurance',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  },

  'adaptive-language-testing-engine': {
    keyBenefits: [
      {
        icon: Languages,
        title: 'AI-Powered Adaptive Algorithm',
        description: 'Advanced machine learning algorithms that personalize difficulty and content based on real-time performance analysis.',
        metrics: '90% more efficient testing',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Brain,
        title: 'Intelligent Question Selection',
        description: 'Smart question bank management with optimal item selection, difficulty calibration, and bias reduction.',
        metrics: '95% assessment precision',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Precision Proficiency Measurement',
        description: 'Accurate language proficiency assessment with statistical validation and psychometric reliability.',
        metrics: '98% measurement accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Zap,
        title: 'Efficient Testing Protocols',
        description: 'Reduced testing time while maintaining accuracy through intelligent adaptive progression and optimization.',
        metrics: '70% faster assessment completion',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Analytics Engine',
        description: 'Comprehensive performance analytics with detailed insights, learning patterns, and improvement recommendations.',
        metrics: 'Deep learning analytics',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Customizable Difficulty Curves',
        description: 'Flexible difficulty adjustment with custom progression curves and institution-specific calibration.',
        metrics: 'Unlimited customisation options',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'Machine Learning Optimization',
        description: 'Continuous algorithm improvement with performance-based learning and automatic calibration refinement.',
        metrics: 'Self-improving assessment accuracy',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Multi-Domain Adaptation',
        description: 'Adaptive testing across multiple language domains with specialized algorithms for different skill areas.',
        metrics: 'Comprehensive skill adaptation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Users,
        title: 'Personalized Learning Pathways',
        description: 'Individual learning path recommendations based on assessment results and performance patterns.',
        metrics: 'Personalized skill development',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: FileCheck,
        title: 'Item Response Theory Engine',
        description: 'Advanced psychometric modeling with IRT calibration, parameter estimation, and statistical validation.',
        metrics: 'Psychometrically sound assessment',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Database,
        title: 'Dynamic Question Bank',
        description: 'Intelligent question bank management with automatic item generation, difficulty estimation, and quality control.',
        metrics: 'Self-expanding question database',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Performance Prediction Engine',
        description: 'Advanced performance prediction with success forecasting and learning outcome optimization.',
        metrics: 'Predictive learning analytics',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Algorithm Security & Integrity',
        description: 'Secure adaptive algorithms with protected models, tamper-proof calculations, and integrity verification.',
        metrics: 'Military-grade algorithm protection',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Adaptive Processing',
        description: 'High-performance adaptive engine with millisecond response times and instant difficulty adjustments.',
        metrics: '<50ms adaptive response',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Secure Assessment Delivery',
        description: 'Protected assessment environment with encrypted delivery, secure computation, and result integrity.',
        metrics: 'End-to-end assessment security',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Clock,
        title: 'High-Performance Computing',
        description: 'Scalable adaptive processing with distributed computing, load balancing, and optimal resource utilization.',
        metrics: '99.99% adaptive engine uptime',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Secure Data Analytics',
        description: 'Protected performance data with encrypted analytics, secure storage, and privacy-compliant processing.',
        metrics: '100% data protection assurance',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Quality Control Automation',
        description: 'Automated quality assurance with algorithm validation, performance monitoring, and continuous improvement.',
        metrics: '99.9% algorithm reliability',
        color: 'from-teal-500 to-blue-600'
      }
    ]
  }
};